import { ScrollArea } from "//de.ringconn.com/cdn/shop/t/60/assets/theme.js?v=54494935086463417971792465359";
import { timeline, stagger } from "//de.ringconn.com/cdn/shop/t/60/assets/vendor.min.js?v=110209841862038618801792465039";

/**
 * 图片文本并排组件
 * 实现水平滚动和前后控制功能
 */
class ImageBelowText extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.children).filter(
      (node) => node.nodeType === Node.ELEMENT_NODE && node.classList.contains("image-text-item"),
    );

    // 初始化滚动区域
    this.scrollArea = new ScrollArea(this);

    this._bindItemFocusA11y();

    // 添加控制事件监听
    this.addEventListener("control:prev", this._prev.bind(this));
    this.addEventListener("control:next", this._next.bind(this));

    // 添加动画效果
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this._setupAnimation();
    }
  }

  _getItemA11yLabel(item) {
    const explicit = item.getAttribute("aria-label")?.trim() || item.getAttribute("data-a11y-label")?.trim();
    if (explicit) {
      return explicit;
    }
    const title = item.querySelector(".image-text_title")?.textContent?.trim();
    if (title) {
      return title.replace(/\s+/g, " ");
    }
    const img = item.querySelector('img[alt]:not([alt=""])');
    return img?.getAttribute("alt")?.trim() || "";
  }

  _childUsesInnerLinkFocus(item) {
    if (!this.hasAttribute("data-a11y-keyboard-links")) {
      return false;
    }
    return Boolean(item.querySelector("a[href]"));
  }

  _getDirectChildFromNode(node) {
    let el = node;
    while (el && el !== this) {
      if (el.parentElement === this && this.items.includes(el)) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  _scrollToIndex(index) {
    const targetItem = this.items[index];
    if (!targetItem) {
      return;
    }

    const containerWidth = this.clientWidth;
    const itemWidth = targetItem.clientWidth;
    const maxScrollLeft = this.scrollWidth - containerWidth;
    let scrollLeft;

    if (index === this.items.length - 1) {
      scrollLeft = maxScrollLeft;
    } else {
      scrollLeft = targetItem.offsetLeft - (containerWidth - itemWidth) / 2;
    }

    scrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));

    this.scrollTo({
      left: scrollLeft,
      behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto",
    });
  }

  /** 轮播宿主不可 Tab；每张 image-text-item 可聚焦 */
  _bindItemFocusA11y() {
    this.removeAttribute("tabindex");
    this.dataset.a11yRegionBound = "false";
    if (!this.hasAttribute("aria-roledescription")) {
      this.setAttribute("aria-roledescription", "carousel");
    }
    const regionLabel = this.getAttribute("data-a11y-label");
    if (regionLabel && !this.hasAttribute("aria-label")) {
      this.setAttribute("aria-label", regionLabel);
    }

    const syncItems = () => {
      this.items.forEach((item) => {
        if (this._childUsesInnerLinkFocus(item)) {
          item.tabIndex = -1;
          item.removeAttribute("role");
          item.removeAttribute("aria-roledescription");
          return;
        }
        item.tabIndex = 0;
        if (!item.hasAttribute("role")) {
          item.setAttribute("role", "group");
        }
        item.setAttribute("aria-roledescription", "slide");
        const label = this._getItemA11yLabel(item);
        if (label) {
          item.setAttribute("aria-label", label);
        }
      });
      if (this.hasAttribute("data-a11y-keyboard-links")) {
        window.A11yLinkKeyboard?.scan?.(this);
      }
    };
    syncItems();

    if (this._itemFocusA11yBound) {
      return;
    }
    this._itemFocusA11yBound = true;

    this.addEventListener("focusin", (event) => {
      const item = this._getDirectChildFromNode(event.target);
      if (!item) {
        return;
      }
      const index = this.items.indexOf(item);
      if (index >= 0) {
        this._scrollToIndex(index);
      }
    });

    this.addEventListener("keydown", (event) => {
      const item = this._getDirectChildFromNode(event.target);
      if (!item) {
        return;
      }
      const index = this.items.indexOf(item);
      if (index === -1) {
        return;
      }

      let targetIndex = null;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          targetIndex = index + 1;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          targetIndex = index - 1;
          break;
        case "Home":
          targetIndex = 0;
          break;
        case "End":
          targetIndex = this.items.length - 1;
          break;
        default:
          return;
      }

      if (targetIndex < 0 || targetIndex >= this.items.length || targetIndex === index) {
        return;
      }

      event.preventDefault();
      const targetItem = this.items[targetIndex];
      this._scrollToIndex(targetIndex);
      if (this._childUsesInnerLinkFocus(targetItem)) {
        targetItem.querySelector("a[href]")?.focus?.();
      } else {
        targetItem.focus();
      }
    });
  }

  /**
   * 设置入场动画
   */
  _setupAnimation() {
    const items = this.querySelectorAll(".image-text-item");
    if (items.length > 0) {
      timeline([
        [
          items,
          { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] },
          { duration: 0.4, delay: stagger(0.1) },
        ],
      ]);
    }
  }

  /**
   * 向前滚动
   */
  _prev() {
    if (!this.items.length) return;
    this.scrollBy({
      left: (document.dir === "rtl" ? 1 : -1) * this.items[0].clientWidth,
      behavior: "smooth",
    });
  }

  /**
   * 向后滚动
   */
  _next() {
    if (!this.items.length) return;
    this.scrollBy({
      left: (document.dir === "rtl" ? -1 : 1) * this.items[0].clientWidth,
      behavior: "smooth",
    });
  }
}

/**
 * 图片在文本下方的组件
 * 继承自ImageTextParallel，保持相同的滚动行为
 */
class ImageTextParallel extends ImageBelowText {
  connectedCallback() {
    super.connectedCallback();

    // 添加响应式处理
    this._setupResponsive();
    this.init();
  }
  init() {
    const parallelId = this.id;
    const id = parallelId.split("image-text-parallel-")[1];
    const pressId = `swiper-parallel-carouse-${id}`;
    // console.log(id, parallelId, pressId, 'id');
    const paralleNextButton = document.querySelector(`button[is="next-button"][aria-controls="${parallelId}"]`);
    const pressNextButton = document.querySelector(`button[is="next-button"][aria-controls="${pressId}"]`);
    const parallePrevButton = document.querySelector(`button[is="prev-button"][aria-controls="${parallelId}"]`);
    const pressPrevButton = document.querySelector(`button[is="prev-button"][aria-controls="${pressId}"]`);
    if (paralleNextButton && pressNextButton) {
      paralleNextButton.addEventListener("click", () => {
        pressNextButton.click();
      });
    }
    if (parallePrevButton && pressPrevButton) {
      parallePrevButton.addEventListener("click", () => {
        pressPrevButton.click();
      });
    }
    // const nextButton = this.querySelector('.next-button');
  }
  /**
   * 设置响应式行为
   */
  _setupResponsive() {
    // 获取父级section以便访问设置
    const parentSection = this.closest(".shopify-section");
    if (!parentSection) return;

    // 监听窗口大小变化
    window.addEventListener("resize", this._handleResize.bind(this));
    this._handleResize();
  }

  /**
   * 处理窗口大小变化
   */
  _handleResize() {
    const isMobile = window.innerWidth < 700;
    this.classList.toggle("is-mobile", isMobile);
  }
}

// 注册自定义元素
if (!window.customElements.get("image-text-parallel")) {
  window.customElements.define("image-text-parallel", ImageTextParallel);
}

if (!window.customElements.get("image-below-text")) {
  window.customElements.define("image-below-text", ImageBelowText);
}

export { ImageTextParallel, ImageBelowText };
